import {
  AppBar,
  AppBarSection,
  AppBarSpacer,
} from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import classNames from "classnames";
import { useAtomValue } from "jotai";
import { Clock } from "lucide-react";
import * as React from "react";
import { If, Then } from "react-if";
import { Outlet, useNavigation, useLocation } from "react-router-dom";
import TrustAIIcon from "@/components/AILanding/icons/TrustAI.svg?react";
import GuardianHost from "@/components/Guardian/GuardianHost";
import { useOptionalGuardianLayout } from "@/components/Guardian/GuardianProvider";
import { SuccessNotification } from "@/components/Kendo/Notification";
import { PageLoadingPanel } from "@/components/LodingPanel";
import Notifications from "@/components/Notifications";
import TrustAISplitterLayout from "@/components/TrustAI/TrustAISplitterLayout";
import {
  isAgentBoardEnabled,
  isGuardianEnabled,
} from "@/configs/agentFeatures";
import { URLs } from "@/configs/menu";
import { useConfigParameters, useUserState } from "@/context/UserContext";
import useAutomationDetection from "@/hooks/useAutomationDetection";
import useRefreshToken from "@/hooks/useRefreshToken";
import Breadcrumbs from "@/layout/Bredcrumbs";
import FreemiumDataBar from "@/layout/FreemiumDataBar";
import FreemiumUpgradeButton from "@/layout/freemiumUpgrade/FreemiumUpgrade";
import UserMenu from "@/layout/v3/UserMenu";
import applicationState from "@/states/app";
import Sidebar from "./Sidebar";
import { SidebarProvider } from "./SidebarContext";
import "./style.scss";

type TrustAISplitterPaneState = {
  size?: string;
  min?: string;
  max?: string;
  resizable?: boolean;
  collapsible?: boolean;
  collapsed?: boolean;
  scrollable?: boolean;
};

const TRUSTAI_LEFT_MIN_WIDTH = "950px";

const LayoutContent: React.FC = () => {
  const guardian = useOptionalGuardianLayout();
  const [warningMessage, setWarningMessage] = React.useState<null | string>(
    null,
  );
  const navigation = useNavigation();
  const isNavigating = Boolean(navigation.location);
  const locationData = useLocation();
  const { user } = useUserState();
  const configParams = useConfigParameters();
  const isPanelAIEnabled = configParams.isPanelAIEnabled === true;
  const appState = useAtomValue(applicationState);
  const isFreemiumTenant = configParams.isFreemiumTenant;
  const tenantId = user?.tenant;

  // `URLs.dashboard` serves two pages: the legacy Security Overview dashboard,
  // and — when `isAgentBoardEnabled` — the TrustAI Executive Board, which owns
  // its own full-bleed header and must not carry the freemium bar.
  const isLegacyDashboardPage = React.useMemo(
    () =>
      locationData.pathname === URLs.dashboard &&
      !isAgentBoardEnabled(configParams),
    [locationData.pathname, configParams],
  );

  // Guardian replaces the legacy Copilot wherever it is enabled.
  const isTrustAISplitterEnabled = React.useMemo(() => {
    if (!isPanelAIEnabled || isGuardianEnabled(configParams)) return false;
    if (!locationData.pathname.startsWith("/app/")) return false;
    return locationData.pathname !== URLs.aiLanding;
  }, [isPanelAIEnabled, locationData.pathname, configParams]);

  const [trustAIPanes, setTrustAIPanes] = React.useState<
    TrustAISplitterPaneState[] | undefined
  >(undefined);

  const toggleTrustAIChat = React.useCallback(() => {
    setTrustAIPanes(prev => {
      const base = prev ?? [
        { resizable: true, scrollable: true, min: TRUSTAI_LEFT_MIN_WIDTH },
        {
          resizable: true,
          scrollable: false,
          size: "420px",
          min: "320px",
          collapsible: true,
          collapsed: true,
        },
      ];

      const right = base[1] ?? {};
      return [
        base[0] ?? {
          resizable: true,
          scrollable: true,
          min: TRUSTAI_LEFT_MIN_WIDTH,
        },
        {
          ...right,
          collapsible: true,
          collapsed: !(right.collapsed === true),
        },
      ];
    });
  }, []);

  React.useEffect(() => {
    if (!isTrustAISplitterEnabled) {
      setTrustAIPanes(undefined);
    }
  }, [isTrustAISplitterEnabled]);

  useRefreshToken(setWarningMessage);
  useAutomationDetection();

  return (
    <SidebarProvider>
      <div className="tlx-v4-main">
        <Sidebar />
        <div
          style={
            {
              "--tlx-guardian-docked-width": guardian?.dockedWidth,
              // What the shell gives up on its right edge. Fixed surfaces
              // pinned over the shell (a full-screen workspace) inset by it.
              "--tlx-shell-inset-end": guardian?.reservesLayoutSpace
                ? guardian.dockedWidth
                : "0px",
            } as React.CSSProperties
          }
          className={classNames("tlx-v4-right", {
            "tlx-v4-right--guardian-docked": guardian?.reservesLayoutSpace,
          })}
        >
          <SuccessNotification
            message={warningMessage}
            show={!!warningMessage}
            onClose={() => setWarningMessage(null)}
            style="warning"
          />
          <div className="tlx-v4-appbar">
            <AppBar
              themeColor={"dark"}
              className="k-appbar-dark--first tlx-v4-topbar"
            >
              <div className="tlx-v4-topbar-wrapper">
                <AppBarSpacer />
                <If condition={isFreemiumTenant}>
                  <Then>
                    <AppBarSection
                      style={{ marginRight: "var(--tlx-space-5)" }}
                    >
                      <FreemiumUpgradeButton />
                    </AppBarSection>
                  </Then>
                </If>
                <AppBarSection>
                  <Tooltip
                    anchorElement="target"
                    position="bottom"
                    style={{ width: 300 }}
                  >
                    <div
                      title="Please note that all time values provided are in the UTC (Coordinated Universal Time) timezone"
                      className="tlx-v4-utc-view"
                    >
                      <Clock />
                      <span>UTC</span>
                    </div>
                  </Tooltip>
                </AppBarSection>
                <AppBarSection>
                  <Notifications />
                </AppBarSection>
                {isTrustAISplitterEnabled && (
                  <AppBarSection>
                    <Tooltip anchorElement="target" position="bottom">
                      <button
                        type="button"
                        className="tlx-v4-trustai-button"
                        title="TrustAI Copilot"
                        aria-label="Toggle TrustAI Copilot"
                        onClick={toggleTrustAIChat}
                      >
                        <TrustAIIcon />
                      </button>
                    </Tooltip>
                  </AppBarSection>
                )}
                <AppBarSection>
                  <UserMenu />
                </AppBarSection>
              </div>
            </AppBar>
            <If condition={isFreemiumTenant && isLegacyDashboardPage}>
              <Then>
                <AppBar className="k-appbar-freemium-data-processed">
                  <FreemiumDataBar
                    isFreemiumTenant={isFreemiumTenant}
                    tenantId={tenantId}
                  />
                </AppBar>
              </Then>
            </If>
            <If condition={appState.hasTabNav}>
              <Then>
                <div className="tlx-v4-full-border-line"></div>
              </Then>
            </If>
          </div>
          <div
            className={classNames("tlx-main-body", "tlx-v4-main-body", {
              "tlx-v4-main-body-trustai": isTrustAISplitterEnabled,
              "tlx-main-body--guardian": !!guardian,
            })}
          >
            <TrustAISplitterLayout
              enabled={isTrustAISplitterEnabled}
              excludedPathname={URLs.aiLanding}
              title="TrustAI Copilot"
              leftMinWidth={TRUSTAI_LEFT_MIN_WIDTH}
              panes={trustAIPanes}
              onPanesChange={setTrustAIPanes}
              left={
                <>
                  <Breadcrumbs />
                  {isNavigating && <PageLoadingPanel />}
                  <Outlet />
                </>
              }
            />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

const Layout: React.FC = () => {
  const { user } = useUserState();
  const configParams = useConfigParameters();
  return (
    <GuardianHost
      enabled={isGuardianEnabled(configParams)}
      sessionKey={`${user?.tenant}:${user?.userId}`}
    >
      <LayoutContent />
    </GuardianHost>
  );
};

export default Layout;
